import React, { ChangeEvent, FunctionComponent, useState } from 'react';
import { Button, Form, Row } from 'react-bootstrap';
import XLSX from 'xlsx';
import { z } from 'zod';
import { ConnectedProps, connect } from 'react-redux';
import {
  AuthedLayout,
  EditableTableDiv,
  HeaderDisplayKeysWithType,
} from '../components';
import Apis from '../apis';
import {
  PhiSundry,
  PhiSundryFile,
  PhiSundryFileZod,
  ClassStudent,
  CheckField,
} from '../model';
import { ErrorDispatches } from '../redux/Dispatches';
import { ApplicationState } from '../redux/States';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import { dateTimeFromString } from '../utils/date';
import {
  CheckShouldUseIdentify,
  assignExists,
  checkDuplicates,
  takeClassStudent,
} from '../utils';
import { useActiveSems } from '../hook';

const inputFileParser = z.array(PhiSundryFileZod);
const headerDisplayKeys: HeaderDisplayKeysWithType<PhiSundryFile>[] = [
  { display: '備註', property: 'remark' },
  { display: '身份證字號', property: 'pid' },
  { display: '學號', property: 'sid' },
  { display: '年級', property: 'grade' },
  { display: '班級', property: 'no' },
  { display: '座號', property: 'seat' },
  { display: '檢查項目', property: 'sundryId' },
  { display: '檢查單位', property: 'agencies' },
  { display: '檢查結果', property: 'sundryResult' },
  {
    display: '檢查日期',
    property: 'examDate',
    onRender: (v) => {
      return v instanceof DateTime ? v.toFormat('yyyy/MM/dd') : v;
    },
  },
];

function filterOutNonPhiSundryField({
  remark,
  examDate,
  ...phiSundry
}: PhiSundryFile): PhiSundry {
  return { ...phiSundry, examDate: dateTimeFromString(examDate || '') };
}

function checkOkToUpload(okUpload: boolean, data: PhiSundryFile[]): boolean {
  // console.log('checkOkToUpload: ',okUpload);
  if (okUpload) {
    const a = data.every(function (item) {
      return item.remark === '';
    });
    // console.log(a);
    return a;
  } else {
    return false;
  }
}

const mapState = (state: ApplicationState) => ({ ...state.auth });
const mapDispatch = ErrorDispatches;
const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;

const importPhiSundryPage: FunctionComponent<Props> = ({
  user: { semesters, currentSemester },
  catchErrorForModal,
  showError,
}) => {
  const [fileName, setFileName] = useState('檔案');
  const [fileData, setFileData] = useState([] as PhiSundryFile[]);
  const [okToUpload, setOkToUpload] = useState(false);
  const [checkField, setCheckField] = useState(CheckField.None);

  const { selectedSemester, yearSemElement } = useActiveSems(
    semesters,
    currentSemester
  );

  return (
    <AuthedLayout>
      <Row className="mb-2">匯入學期：{yearSemElement}</Row>
      <Row className="mb-2">
        下載匯入格式：
        <a href="/Doc/臨時性檢查12.xls">臨時性檢查匯入格式</a>
      </Row>
      <Row className="mb-2">
        <Form.File
          type="file"
          className="my-file-label"
          id="inputGroupFile01"
          label={fileName}
          accept=".xlsx, .csv, .xls, .ods, .ots"
          onChange={async (e: ChangeEvent<HTMLInputElement>) => {
            e.persist();
            const { files } = e.target;
            try {
              if (files?.length === 1) {
                setFileName(files[0].name);
                const buf = await files[0].arrayBuffer();
                const workShop = XLSX.read(buf, {
                  type: 'buffer',
                  sheets: 0,
                });
                if (workShop.SheetNames[0]) {
                  const jsonData = XLSX.utils.sheet_to_json(
                    workShop.Sheets[workShop.SheetNames[0]],
                    { raw: false, rawNumbers: false }
                  );
                  const headerData = XLSX.utils.sheet_to_json(
                    workShop.Sheets[workShop.SheetNames[0]],
                    { header: 1 }
                  );
                  const headers = headerData[0] as string[];
                  const isCorrectFile = headers.find(
                    (h: string, i: number) => h === '檢查項目代號'
                  );
                  // 檢查表頭是否正確
                  if (!isCorrectFile) {
                    showError(
                      'Excel中的表頭欄位不正確，請確認是否選擇了正確的檔案。'
                    );
                    return;
                  }
                  const ss = await inputFileParser.parseAsync(
                    jsonData.map((j: any) => ({ ...j, ...selectedSemester }))
                  );
                  if (ss) {
                    const result = checkDuplicates(ss);
                    if (
                      result.duplicatePids.length ||
                      result.duplicateSids.length ||
                      result.duplicateGradeGroups.length
                    ) {
                      showError(
                        '匯入資料重複 ' +
                          (result.duplicatePids.join('、') ||
                            result.duplicateSids.join('、') ||
                            result.duplicateGradeGroups.join('、'))
                      );
                      return;
                    }
                    const [cf, matchFunc] = CheckShouldUseIdentify(ss);
                    setCheckField(cf);
                    if (cf == CheckField.None) {
                      showError(
                        '匯入之檔案應有唯一使用"身份證"或"學號"或"班級座號"'
                      );
                      return;
                    }
                    await Apis.checkClassesStudentExists(
                      takeClassStudent(ss),
                      cf,
                      selectedSemester
                    )
                      .then((result) => {
                        // console.log('importPhiSundry.ts #104',result)
                        // setFileData (merge result&ss)
                        setFileData(assignExists(matchFunc, ss, result));
                        //check ss is okToUpload ,then setOkToUpload(true);
                        setOkToUpload(checkOkToUpload(true, ss));
                      })
                      .catch((e) => {
                        catchErrorForModal(e);
                        setOkToUpload(false);
                      });
                  } else {
                    console.log(ss);
                  }
                }
              }
            } catch (e) {
              console.log('importPhiSundry.ts #117', e);
              setFileName('檔案');
              setFileData([]);
              setOkToUpload(false);
              showError('檔案內容不正確，請檢查資料內容格式');
            }
            e.target.value = '';
          }}
          custom
        />
        <Button
          className="mr-0"
          disabled={!okToUpload}
          onClick={() => {
            if (fileData.length > 0 && checkField != CheckField.None)
              toast
                .promise(
                  Apis.insertPhiSundry(
                    fileData.map(filterOutNonPhiSundryField),
                    checkField,
                    selectedSemester
                  ),
                  {
                    pending: '資料上傳中......',
                    success: '上傳成功！',
                    error: '上傳失敗！請查看錯誤資訊。',
                  }
                )
                .then(() => {
                  setOkToUpload(false);
                })
                .catch(catchErrorForModal);
          }}
        >
          匯入資料
        </Button>
      </Row>
      {
        <Row>
          <EditableTableDiv
            fixCol={2}
            headers={headerDisplayKeys}
            values={fileData}
            onRowRender={({ examDate, ...rest }) => ({
              rowClassName: rest.remark ? 'bg-danger text-white' : '',
            })}
          />
        </Row>
      }
    </AuthedLayout>
  );
};

export const ImportPhiSundryPage = connector(importPhiSundryPage);
